import React from 'react';
import Layout from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell, Clock, Coffee, Apple, UtensilsCrossed } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';

// Sample notification data
const notifications = [
  {
    id: 1,
    type: 'food',
    subtype: 'breakfast',
    read: false,
    time: '08:00 AM',
    date: '2023-09-15'
  },
  {
    id: 2,
    type: 'food',
    subtype: 'lunch',
    read: true,
    time: '01:00 PM',
    date: '2023-09-15'
  },
  {
    id: 3,
    type: 'food',
    subtype: 'dinner',
    read: false,
    time: '07:00 PM',
    date: '2023-09-14'
  },
  {
    id: 4,
    type: 'medication',
    read: true,
    time: '10:00 AM',
    date: '2023-09-14'
  },
  {
    id: 5,
    type: 'update',
    read: false,
    time: '11:30 AM',
    date: '2023-09-13'
  }
];

const NotificationItem = ({ notification }: { notification: typeof notifications[0] }) => {
  const { t } = useTranslation();
  
  const getIcon = (type: string) => {
    switch (type) {
      case 'food':
        return <UtensilsCrossed className="text-sangre-600" />;
      case 'medication':
        return <Coffee className="text-fuerte-600" />;
      case 'update':
        return <Apple className="text-green-500" />;
      default:
        return <Bell className="text-gray-500" />;
    }
  };
  
  const getTitle = () => {
    if (notification.type === 'food' && notification.subtype) {
      return t(`notifications.types.food.${notification.subtype}.title`);
    }
    return t(`notifications.types.${notification.type}.title`);
  };
  
  const getMessage = () => {
    if (notification.type === 'food' && notification.subtype) {
      return t(`notifications.types.food.${notification.subtype}.message`);
    }
    return t(`notifications.types.${notification.type}.message`);
  };
  
  return (
    <Card className={`p-4 mb-3 border-l-4 ${notification.read ? 'border-l-gray-200' : 'border-l-sangre-600'}`}>
      <div className="flex items-start">
        <div className="p-2 rounded-full bg-gray-50 mr-3">
          {getIcon(notification.type)}
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h5 className="font-medium text-gray-900">{getTitle()}</h5>
              <p className="text-sm text-gray-600 mt-1">{getMessage()}</p>
            </div>
            {!notification.read && (
              <Badge variant="outline" className="bg-sangre-100 text-sangre-600 border-none">
                {t('notifications.new')}
              </Badge>
            )}
          </div>
          <div className="flex items-center mt-2 text-xs text-gray-500">
            <Clock className="w-3 h-3 mr-1" />
            <span>{notification.time} • {notification.date}</span>
          </div>
        </div>
      </div>
    </Card>
  );
};

const NotificationsScreen = () => {
  const { t } = useTranslation();
  
  return (
    <Layout title={t('notifications.title')} showBackButton>
      <div className="pb-16 px-4">
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-1">{t('notifications.title')}</h2>
          <p className="text-gray-600 text-sm">{t('notifications.subtitle')}</p>
        </div>
        
        <ScrollArea className="h-[calc(100vh-180px)]">
          <div className="pr-4">
            {notifications.length > 0 ? (
              notifications.map(notification => (
                <NotificationItem key={notification.id} notification={notification} />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center h-60 text-center">
                <Bell className="w-10 h-10 text-gray-300 mb-4" />
                <h3 className="text-gray-500 font-medium">{t('notifications.noNotifications')}</h3>
                <p className="text-gray-400 text-sm mt-1">{t('notifications.noNotificationsSubtitle')}</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </Layout>
  );
};

export default NotificationsScreen;
